import {
  DropDownMenuProps,
  DropDownMenu,
  CustomTooltip,
} from '@tcweb/connect-ui';
import { ModusWcCheckboxCustomEvent } from '@trimble-oss/moduswebcomponents';
import { ICONS } from './constants';
import {
  Accessor,
  children,
  Component,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  Show,
  useContext,
} from 'solid-js';

export interface TreeNode {
  label: string;
  labelColor?: string;
  labelTooltip?: string;
  highlightText?: string;
  isExpanded: boolean;
  statusMessage?: string;
  statusMessageColor?: string;
}

export function produceTreeNode(
  label: string,
  isExpanded: boolean,
  highlightText?: string,
  labelColor?: string,
  statusMessage?: string,
  labelTooltip?: string,
  statusMessageColor?: string
): TreeNode {
  return {
    isExpanded: isExpanded,
    highlightText: highlightText,
    label: label,
    labelColor: labelColor,
    statusMessage: statusMessage,
    labelTooltip: labelTooltip,
    statusMessageColor: statusMessageColor,
  };
}

interface TreeItemProps {
  node: TreeNode;
  disabled?: boolean;
  class?: string;
  children?: JSX.Element;
  hasChildren?: boolean;
  // This is content that is always shown, no matter if node is collapsed or not
  // i.e. children can be collapsed.
  visibleContent?: Component;
  /** Visual to display before the label.*/
  prefixContent?: Component;
  /**
   * When true, shows a selection checkbox instead of prefix content
   * (visibility toggle and any left-of-label icons).
   */
  showCheckbox?: boolean;
  /** Visual to display inline after the label. */
  labelSuffix?: Component;
  onExpandToggle?(isExpanded: boolean): void;
  onSelect?(e: MouseEvent): void;
  selected?: boolean;
  allowSelectFolder?: boolean;
  contextMenuOptions?: TreeItemContextMenuOption[];
  onContextMenuMount?(): void;
  onContextMenuUnmount?(): void;
  /** Overrides context-derived indentation for flat tree renderers. */
  depth?: number;
}

export type TreeItemContextMenuOption = {
  text: string;
  onClick(): void;
  disabled?(): boolean;
  disabledReasonTooltip?: string;
  icon?: string;
};

/**
 * `TreeDepthContext` is used to track the nesting level of the tree item.
 * The value is then used to determine the padding/indentation of the tree item.
 */
const TreeDepthContext = createContext(0);
const LEFT_MARGIN_PER_LEVEL = 1; // Rem units per tree level.

export const TreeItem: Component<TreeItemProps> = (
  props: TreeItemProps
): JSX.Element => {
  const fileDropdownProps: Accessor<DropDownMenuProps> = createMemo(
    () =>
      ({
        type: 'icon',
        anchorIcon: 'icon-font tc-icon-more-vert',
        optionsType: 'both',
        position: 'right',
        iconPosition: 'left',
        iconTooltip: 'Options',
        dropDownOptions:
          props.contextMenuOptions?.map((o) => ({
            text: o.text,
            icon: o.icon,
            disabled: o.disabled?.(),
            disabledReasonTooltip: o.disabledReasonTooltip,
          })) ?? [],
        showOnScroll: true,
        unsetHeight: true,
        dontCloseOnClick: false,
        disabledItem:
          props.contextMenuOptions
            ?.map((option, index) => (option.disabled?.() ? index : -1))
            .filter((index) => index !== -1) ?? [],
        onSelectionChanged: (_selected, index) => {
          props.contextMenuOptions?.[index]?.onClick();
        },
        onMenuContentMount: props.onContextMenuMount,
        onMenuContentUnmount: props.onContextMenuUnmount,
      }) satisfies DropDownMenuProps
  );

  const contextIndentLevel = useContext(TreeDepthContext);
  const indentLevel = (): number => props.depth ?? contextIndentLevel;
  const resolvedChildren = children(() => props.children);

  const hasChildren = (): boolean => {
    if (props.hasChildren) return true;

    const child = resolvedChildren();
    return Array.isArray(child) ? child.length > 0 : !!child;
  };

  // Function to render the label with matched text highlighted
  const renderLabel = createMemo((): (string | JSX.Element)[] => {
    const label = props.node.label;
    const filter = props.node.highlightText?.toLowerCase();
    if (!filter) {
      // If no filter or node is not set to highlight, return label as is
      return [label];
    }

    const regex = new RegExp(`(${filter})`, 'gi');
    const parts = label.split(regex);

    return parts.map((part) => {
      if (part.toLowerCase() === filter) {
        return <span class="text-blue font-bold">{part}</span>;
      } else {
        return part;
      }
    });
  });

  const [isExpanded, setIsExpanded] = createSignal<boolean>(false);
  createEffect((prev) => {
    if (prev != props.node.isExpanded) {
      setIsExpanded(props.node.isExpanded);
    }
  });
  const handleExpandToggle = (isExpanded: boolean): void => {
    setIsExpanded(isExpanded);
    if (props.onExpandToggle) {
      props.onExpandToggle(isExpanded);
    }
  };

  const canSelectItem = (): boolean => {
    const isParent = hasChildren();
    return (
      !!props.onSelect &&
      !props.disabled &&
      (props.allowSelectFolder || !isParent)
    );
  };

  const handleCheckboxInput = (
    event: ModusWcCheckboxCustomEvent<InputEvent>
  ): void => {
    event.stopPropagation();
    if (!canSelectItem()) return;

    const target = event.detail.target as HTMLInputElement;
    if (target.checked === Boolean(props.selected)) return;

    // ctrlKey makes multi-select toggle this row without replacing others.
    props.onSelect?.(
      new MouseEvent('click', {
        bubbles: false,
        cancelable: true,
        ctrlKey: true,
      })
    );
  };

  const treeItemClasses = createMemo(() => {
    const classes = [
      'flex flex-row select-none border-l-[0.2rem] border-[transparent] items-center',
    ];

    classes.push(props.node.statusMessage === undefined ? 'h-9' : 'h-11');

    if (!props.disabled) {
      if (hasChildren() || !!props.onSelect) {
        classes.push('cursor-pointer');
      }
      classes.push(
        props.selected ? 'hover:bg-primary-pale' : 'hover:bg-base-100'
      );
    }

    if (props.selected) {
      classes.push('!border-primary bg-primary-pale');
    }

    if (props.class) {
      classes.push(props.class);
    }

    return classes.join(' ');
  });

  /* eslint-disable @typescript-eslint/naming-convention */
  return (
    // eslint-disable-next-line solid/reactivity -- A mounted tree item's depth is immutable.
    <TreeDepthContext.Provider value={indentLevel() + 1}>
      <div
        data-testid="tree-expand"
        class={treeItemClasses()}
        style={{
          'padding-left': indentLevel()
            ? `${String(indentLevel() * LEFT_MARGIN_PER_LEVEL)}rem`
            : '0',
        }}
        aria-expanded={hasChildren() ? isExpanded() : undefined}
        aria-selected={props.selected}
        onClick={(e) => {
          const isParent = hasChildren();
          const canSelect = !!props.onSelect && !props.disabled;
          // Selectable folders and leaves select on row click; the chevron
          // handles expansion for selectable folders.
          if (canSelect && (props.allowSelectFolder || !isParent)) {
            props.onSelect?.(e);
          } else if (isParent) {
            handleExpandToggle(!isExpanded());
          }
        }}
      >
        <Show when={hasChildren()}>
          <div
            class="flex w-10 h-10 rounded-full items-center justify-center hover:bg-base-200"
            onClick={(e) => {
              // When the row is selectable the row click no longer toggles
              // expansion, so the chevron must do it explicitly.
              if (props.allowSelectFolder) {
                e.stopPropagation();
                handleExpandToggle(!isExpanded());
              }
            }}
          >
            <i
              class={[
                'icon-font',
                isExpanded() ? ICONS.expanded : ICONS.collapsed,
              ].join(' ')}
            />
          </div>
        </Show>
        <Show
          when={props.showCheckbox}
          fallback={
            // eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38
            props.prefixContent ? props.prefixContent({}) : null
          }
        >
          <div
            class="flex items-center p-2"
            data-testid="tree-item-checkbox"
            onClick={(e) => e.stopPropagation()}
          >
            <modus-wc-checkbox
              aria-label={props.node.label}
              disabled={props.disabled}
              label=""
              size="md"
              value={props.selected ?? false}
              on:inputChange={handleCheckboxInput}
            />
          </div>
        </Show>
        <div class="flex flex-1 flex-col justify-center overflow-hidden">
          <div
            class={props.labelSuffix ? 'flex min-w-0 items-center' : 'contents'}
          >
            <div
              class={props.labelSuffix ? 'min-w-0 overflow-hidden' : 'contents'}
            >
              <CustomTooltip
                text={props.node.labelTooltip ?? props.node.label}
                position="bottom"
                class={
                  props.labelSuffix
                    ? `${props.node.labelColor ?? ''} max-w-full`
                    : (props.node.labelColor ?? '')
                }
                tooltipClass="!max-w-fit"
              >
                <span
                  class={`block truncate text-sm font-normal ${
                    props.selected ? 'text-primary font-semibold' : ''
                  }`}
                >
                  {renderLabel()}
                </span>
              </CustomTooltip>
            </div>
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {props.labelSuffix ? props.labelSuffix({}) : null}
          </div>
          <Show when={props.node.statusMessage != null}>
            <CustomTooltip
              text={props.node.statusMessage}
              position="bottom"
              class={props.node.statusMessageColor ?? ''}
              tooltipClass="!max-w-fit"
            >
              <span class="block truncate text-xs leading-5 italic">
                {props.node.statusMessage}
              </span>
            </CustomTooltip>
          </Show>
        </div>
        {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
        {props.visibleContent ? props.visibleContent({}) : null}
        <Show when={fileDropdownProps().dropDownOptions.length > 0}>
          <div class="justify-self-end mr-2">
            <div
              class="flex w-10 h-10 rounded-full items-center justify-center text-base-content"
              data-testid="tree-item-dropdown"
            >
              <DropDownMenu {...fileDropdownProps()} />
            </div>
          </div>
        </Show>
      </div>
      <Show when={isExpanded() && hasChildren()}>{props.children}</Show>
    </TreeDepthContext.Provider>
  );
};

export default TreeItem;
